import type { ReactNode } from "react"
import { useMemo } from "react"
import { Link } from "@tanstack/react-router"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconSquareArrowUpRightOutline18 as OpenIcon,
	IconPlusOutline18 as Plus,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { scale } from "@productlane/lib/src/plans-client"
import { Avatar, Badge, Button, IconButton } from "@productlane/ui"

import { UpgradeButton } from "@/components/upgrade-button"
import { useOpenNewPortal } from "@/lib/hooks/use-open-new-portal"
import { useRequiredPlans } from "@/lib/plans"
import { portalColorSeeds } from "@/lib/portal-instances"
import { useZeroWorkspace } from "@/lib/zero"
import {
	portalHostname,
	portalPublicUrl,
} from "@/screens/settings/portal-instance-form"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"

const DIVIDER = "border-gray-200 dark:border-gray-700/80"

const ROW_CLASS = cn(
	"group flex items-center gap-3 border-b px-4 py-4 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50",
	DIVIDER,
)

/**
 * Portal list for /settings/portal and /settings/portal/instances. The first row
 * is the main portal, which has no PortalInstance row: it serves the unprefixed
 * domain and owns every item with no portal of its own.
 */
export function PortalInstancesSection() {
	const workspace = useZeroWorkspace()
	const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)
	const openNewPortal = useOpenNewPortal()

	const instances = useMemo(() => {
		const list = workspace?.portalInstances ?? []
		return [...list].sort((a, b) => a.name.localeCompare(b.name))
	}, [workspace?.portalInstances])

	const defaultInstance = instances.find((i) => i.isDefault) ?? null

	if (!workspace) return null

	const colorSeeds = portalColorSeeds(instances)

	// Main is always a portal, so the count is never zero and there is no empty state.
	const total = instances.length + 1

	return (
		<SettingsContainer className="mb-0 mt-0 overflow-hidden px-0 pb-0">
			<div className="flex flex-col">
				<div
					className={cn(
						"flex items-center justify-between border-b px-4 pb-4",
						DIVIDER,
					)}
				>
					<div className="text-sm font-medium">
						{total} {total === 1 ? "portal" : "portals"}
					</div>
					{hasRequiredPlan ? (
						<Button
							variant="ghost"
							Icon={Plus}
							className="text-primary"
							onClick={openNewPortal}
						>
							New portal
						</Button>
					) : (
						<UpgradeButton required={scale.andUp} />
					)}
				</div>

				<PortalRow
					to="/settings/portal/instances/main"
					name="Main portal"
					url={
						defaultInstance
							? `${portalHostname(workspace)}/main`
							: portalHostname(workspace)
					}
					href={portalPublicUrl(workspace, null, defaultInstance !== null)}
					logoUrl={workspace.logoUrl}
					colorSeed={colorSeeds.get(null)}
					badge={
						defaultInstance ? null : (
							<Badge variant="green" size="sm">
								Default
							</Badge>
						)
					}
					sections={sectionList({
						docs: workspace.helpCenterPublic,
						changelog: workspace.changelogPublic,
						requests: workspace.domainPublic,
						support: workspace.customerPortalSettings?.isEnabled ?? false,
					})}
				/>
				{instances.map((instance) => (
					<PortalRow
						key={instance.id}
						to={`/settings/portal/instances/${instance.id}`}
						name={instance.name}
						url={`/${instance.slug}`}
						href={portalPublicUrl(
							workspace,
							instance,
							defaultInstance !== null,
						)}
						logoUrl={instance.logoUrl ?? null}
						colorSeed={colorSeeds.get(instance.id)}
						badge={
							<>
								{instance.isDefault ? (
									<Badge variant="green" size="sm">
										Default
									</Badge>
								) : null}
								{instance.showInSelector ? null : (
									<Badge variant="default" size="sm">
										No switcher
									</Badge>
								)}
							</>
						}
						sections={sectionList({
							docs: instance.showDocs,
							changelog: instance.showChangelog,
							requests: instance.showRoadmap,
							support: instance.showSupport,
						})}
					/>
				))}
			</div>
		</SettingsContainer>
	)
}

function PortalRow({
	to,
	name,
	url,
	href,
	logoUrl,
	colorSeed,
	badge,
	sections,
}: {
	to: string
	name: string
	url: string
	href: string
	logoUrl: string | null
	colorSeed?: string
	badge: ReactNode
	sections: string
}) {
	return (
		<Link to={to} className={ROW_CLASS}>
			<Avatar
				name={name}
				image={logoUrl}
				colorSeed={colorSeed}
				roundness="sm"
				size="sm"
			/>
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-x-2 text-sm">
					<span className="truncate font-medium">{name}</span>
					<span className="font-mono text-secondary">{url}</span>
					{badge}
				</div>
				<div className="truncate text-xs text-secondary">{sections}</div>
			</div>
			<IconButton
				variant="dark-ghost"
				size="md"
				Icon={OpenIcon}
				aria-label={`Open ${name} in a new tab`}
				title="Open in a new tab"
				className="flex-none rounded-md opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
				onClick={(e) => {
					e.preventDefault()
					e.stopPropagation()
					window.open(href, "_blank", "noopener")
				}}
			/>
			<ChevronRight className="size-3 flex-none text-tertiary" />
		</Link>
	)
}

/** The portal nav, in the order the portal itself renders it. */
function sectionList(sections: {
	docs: boolean
	changelog: boolean
	requests: boolean
	support: boolean
}): string {
	const enabled = [
		sections.docs ? "Docs" : null,
		sections.changelog ? "Changelog" : null,
		sections.requests ? "Requests" : null,
		sections.support ? "Support" : null,
	].filter((label): label is string => label !== null)

	return enabled.length ? enabled.join(", ") : "No sections turned on"
}
